/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Grid, Paper, Typography, colors } from "@mui/material"
import { graphql } from "../../../gql"
import { useNavigate, useParams } from "react-router-dom"
import { useLazyQuery } from "@apollo/client"
import HeaderAdd from "../../Common/HeaderAdd/HeaderAdd"
import { useCallback, useEffect, useMemo } from "react"
import Loader from "../../Common/Loader/Loader"
import { setTitle } from "../../../store/features/menu/menuSlice"
import { useAppDispatch } from "../../../store/app/hooks"
import NoDataStatistic from "../../Common/NoDataStatistic/NoDataStatistic"
import Chart from "../../Common/Chart/Chart"
import { havePermissions, isSuperAdmin } from "../../../utils/permissions"
import { getUserCountryId } from "../../../utils/authToken"
import useLang from "../../../hooks/useLang"
import ListOrder from "../../Order/ListOrder/ListOrder"

const GET_CUSTOMER = graphql(`
  query GetDetailCustomerQuery(
    $where: UserWhereUniqueInput!
    $aggregateOrderWhere2: OrderWhereInput
  ) {
    user(where: $where) {
      id
      phoneNumber
      email
      role
      name
      lastName
      lang
      ProfilePhoto {
        url
      }
      country {
        id
        name_fr
        currency
      }
    }
    aggregateOrder(where: $aggregateOrderWhere2) {
      _count {
        _all
      }
    }
  }
`)

interface Props {
  customerId?: number
}

const DetailUser = ({ customerId }: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const params = useParams()
  const { getText } = useLang()
  const [getCustomer, { data, loading }] = useLazyQuery(GET_CUSTOMER)
  const colorsGlobal = [colors.blue[400], colors.green[400]]

  const id = useMemo(
    () => (customerId ? customerId : params.id ? parseInt(params.id, 10) : 0),
    [customerId, params],
  )

  useEffect(() => {
    getCustomer({
      variables: {
        where: {
          id,
        },
        aggregateOrderWhere2: {
          customerId: {
            equals: id,
          },
        },
      },
      onCompleted(data) {
        if (!isSuperAdmin()) {
          if (data.user?.country?.id != getUserCountryId()) {
            navigate("/")
          }
        }
      },
    })
  }, [id])

  const handleEdit = (id: number) => {
    dispatch(setTitle(getText("Modifier un client")))
    navigate(`/user/edit/${id}`)
  }

  const getStatistic = useCallback(() => {
    const value_order = data?.aggregateOrder?._count?._all || 0
    return !value_order
      ? []
      : [
          {
            label: "Nombre commande",
            value: value_order,
            color: colorsGlobal[0],
          },
        ]
  }, [data])

  const handleAddOrder = () => {
    dispatch(setTitle(getText("Ajouter une commande")))
    navigate(`/order/add`)
  }

  if (loading) return <Loader />

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <HeaderAdd
                    title={getText("Information de client")}
                    variant="h4"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Typography>
                        <b>ID:</b> {data?.user?.id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>
                        <b>{getText("Prénom")}:</b> {data?.user?.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>
                        <b>{getText("Pays")}:</b> {data?.user?.country?.name_fr}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>
                        <b>{getText("Devise")}:</b>{" "}
                        {data?.user?.country?.currency}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container mt={3}>
                    <Grid
                      item
                      xs={12}
                      display={"flex"}
                      justifyContent={"flex-end"}
                    >
                      {havePermissions(["customer.change"]) && (
                        <Button
                          onClick={() => handleEdit(data?.user?.id || 0)}
                          sx={{ mr: 1 }}
                          color="info"
                          variant="outlined"
                          size="small"
                        >
                          {getText("Modifier")}
                        </Button>
                      )}
                      {havePermissions(["order.create"]) && (
                        <Button
                          onClick={handleAddOrder}
                          color="success"
                          variant="outlined"
                          size="small"
                        >
                          {getText("Ajouter une commande")}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 2,
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <HeaderAdd
                    title={getText("Statistique de client")}
                    variant="h4"
                  />
                </Grid>
                <Grid item xs={12}>
                  {getStatistic().length ? (
                    <Chart data={getStatistic()} />
                  ) : (
                    <NoDataStatistic />
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid container>
            <Grid item xs={12}>
              <HeaderAdd title={getText("Liste des commandes")} variant="h4" />
            </Grid>
            <Grid item xs={12} mt={2}>
              <ListOrder
                isOnlyCustomer
                extraQuery={{
                  customerId: {
                    equals: id,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  )
}

export default DetailUser
